import { useAtomValue } from 'jotai';
import { useCallback, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useCreateCompanyAndUpdateState } from '../../../../services/hooks/useCreateCompanyAndUpdateState';
import { useListUploadActions } from '../../../../services/hooks/useListActions';
import { companyToSearchResultWithRealId } from '../../../../util/companyUtils';
import { captureFormValidationError } from '../../../../util/SentryUtils';
import {
  IDigestPanelViewModel,
  toCompanyDataModel,
} from '../../../CompanyProfiles2/Summary/DigestPanel.view-model';
import { removeEmptyCustomData } from '../../../Finance2/Forms/CompanyFormUtils';
import { activeListStateJ } from '../../../../services/state/ListsStateJ';

export function useCreateListCompany() {
  const createCompany = useCreateCompanyAndUpdateState();
  const [loading, setLoading] = useState(false);
  const activeList = useAtomValue(activeListStateJ);
  const { addCompanyToList } = useListUploadActions();

  const createListCompany = useCallback(
    async ({
      data,
      errors,
      onClose,
      trigger,
    }: {
      data: Partial<IDigestPanelViewModel>;
      errors: UseFormReturn['formState']['errors'];
      onClose: () => void;
      trigger: UseFormReturn['trigger'];
    }) => {
      const isValid = await trigger();
      if (!isValid) {
        captureFormValidationError('Lists: Create Company Form', errors);
        return;
      }
      setLoading(true);
      const company = await createCompany(prepareCompanyPayload(data));
      if (company && activeList) {
        await addCompanyToList(activeList, companyToSearchResultWithRealId(company));
      }
      setLoading(false);
      onClose();
    },
    [activeList, addCompanyToList, createCompany]
  );

  return { createListCompany, loading };
}

function prepareCompanyPayload(data: Partial<IDigestPanelViewModel>) {
  const payload = toCompanyDataModel(data);
  payload.customData = removeEmptyCustomData(payload.customData ?? null);
  return payload;
}
